import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Menu, Users, FileSpreadsheet, FileText, Cake, Loader2, Gift, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { OwnerSidebar } from '@/components/OwnerSidebar';
import { HelpButton } from '@/components/HelpButton';
import { useAuth } from '@/context/AuthContext';
import { exportExcel, exportPdfTable, fmtDate, fmtPhone } from '@/lib/exporters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho',
  'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const MyCustomers = () => {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ total: 0, filtered: 0, items: [] });
  const [f, setF] = useState({ birthday_month: '', birthday_days: '', date_from: '', date_to: '', ddd: '', rating_type: '', order: 'recent' });
  const [birthdayOpen, setBirthdayOpen] = useState(false);
  const [sendingBirthday, setSendingBirthday] = useState(false);
  const [discount, setDiscount] = useState('');
  const [coupon, setCoupon] = useState('ANIVERSARIO');

  const toggleRedeem = async (contact) => {
    try {
      await axios.post(`${API}/company/contacts/${contact.id}/redeem`, {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success(contact.coupon_redeemed_at ? 'Cupom marcado como não usado' : 'Cliente voltou! Cupom usado 🎉');
      load();
    } catch { toast.error('Erro ao marcar o cupom'); }
  };
  const [birthdayMsg, setBirthdayMsg] = useState(
    'Olá [nome]! 🎉 Hoje é dia de festa e a [empresa] quer comemorar com você: é só mostrar esta mensagem para ganhar [desconto] na sua próxima visita. Parabéns e muitos anos de vida!'
  );

  const sendBirthday = async () => {
    if (!window.confirm(`Enviar a mensagem de parabéns para ${data.items.length} cliente(s) pelo WhatsApp?`)) return;
    setSendingBirthday(true);
    try {
      const res = await axios.post(`${API}/company/contacts/birthday-greetings`, {
        month: Number(f.birthday_month) || new Date().getMonth() + 1, message: birthdayMsg, discount,
        coupon_code: coupon, contact_ids: data.items.map(c => c.id),
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success(`Parabéns enviado para ${res.data.sent} de ${res.data.total} cliente(s)`);
      if (res.data.skipped?.length) {
        toast.info(`${res.data.skipped.length} não receberam: ${res.data.skipped.slice(0, 3).map(s => `${s.name} (${s.reason})`).join(' · ')}`,
          { duration: 9000 });
      }
      setBirthdayOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao enviar'); }
    finally { setSendingBirthday(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== '' && v !== null));
      const res = await axios.get(`${API}/company/contacts`, {
        params, headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setData(res.data);
    } catch { toast.error('Erro ao carregar contatos'); }
    finally { setLoading(false); }
  }, [f]);

  useEffect(() => { load(); }, [load]);

  const rows = data.items.map(c => ({
    Nome: c.name,
    WhatsApp: fmtPhone(c.whatsapp),
    'E-mail': c.email,
    Nascimento: fmtDate(c.birth_date),
    Avaliação: c.rating_type === 'positive' ? 'Positiva' : 'Negativa',
    Cupom: c.coupon_code || '',
    'Voltou com o cupom': c.coupon_redeemed_at ? 'SIM' : '',
    Cadastro: fmtDate(c.created_at),
  }));

  const handleExcel = () => {
    if (!rows.length) return toast.error('Nada para exportar');
    exportExcel(rows, `meus-clientes-${new Date().toISOString().slice(0, 10)}`, 'Clientes');
  };
  const handlePdf = () => {
    if (!rows.length) return toast.error('Nada para exportar');
    exportPdfTable('Meus Clientes', Object.keys(rows[0]), rows.map(r => Object.values(r)),
      `meus-clientes-${new Date().toISOString().slice(0, 10)}`,
      `${user?.company_name || ''} · ${rows.length} contato(s)`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="hidden lg:block w-64 bg-[#0A2647] fixed inset-y-0">
        <OwnerSidebar user={user} handleLogout={logout} activePage="clientes" />
      </aside>
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <div className="relative w-64 bg-[#0A2647]">
            <OwnerSidebar user={user} mobile setSidebarOpen={setSidebarOpen} handleLogout={logout} activePage="clientes" />
          </div>
        </div>
      )}

      <div className="lg:ml-64">
        <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-4 flex items-center gap-3">
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)} data-testid="open-sidebar">
            <Menu className="w-6 h-6 text-slate-700" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-[#0A2647]">Meus Clientes</h1>
            <p className="text-xs text-slate-500">Quem avaliou pelo seu QR Code</p>
          </div>
        </header>

        <main className="p-4 sm:p-6 space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-3" data-testid="contacts-counter">
            <Users className="w-8 h-8 text-[#C5A059]" />
            <div>
              <p className="text-2xl font-bold text-[#0A2647]">{data.total}</p>
              <p className="text-sm text-slate-500">
                clientes cadastrados{data.filtered !== data.total ? ` · ${data.filtered} no filtro atual` : ''}
              </p>
            </div>
            {!!data.redeemed && (
              <div className="ml-auto text-right" data-testid="redeemed-counter">
                <p className="text-2xl font-bold text-emerald-600">{data.redeemed}</p>
                <p className="text-xs text-slate-500">voltaram com o cupom</p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3" data-testid="contacts-filters">
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
              <select value={f.birthday_days} onChange={e => setF({ ...f, birthday_days: e.target.value, birthday_month: '' })}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="filter-birthday-days">
                <option value="">Aniversários: qualquer data</option>
                <option value="7">Próximos 7 dias</option>
                <option value="15">Próximos 15 dias</option>
                <option value="30">Próximos 30 dias</option>
              </select>
              <select value={f.birthday_month} onChange={e => setF({ ...f, birthday_month: e.target.value, birthday_days: '' })}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="filter-birthday">
                <option value="">Aniversariantes: todos</option>
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
              <input type="date" value={f.date_from} onChange={e => setF({ ...f, date_from: e.target.value })}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="filter-from" />
              <input type="date" value={f.date_to} onChange={e => setF({ ...f, date_to: e.target.value })}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="filter-to" />
              <input placeholder="DDD" maxLength={2} value={f.ddd} onChange={e => setF({ ...f, ddd: e.target.value.replace(/\D/g, '') })}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="filter-ddd" />
              <select value={f.rating_type} onChange={e => setF({ ...f, rating_type: e.target.value })}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="filter-rating">
                <option value="">Avaliação: todas</option>
                <option value="positive">Positivas</option>
                <option value="negative">Negativas</option>
              </select>
              <select value={f.order} onChange={e => setF({ ...f, order: e.target.value })}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="filter-order">
                <option value="recent">Mais recentes</option>
                <option value="az">Nome A-Z</option>
                <option value="za">Nome Z-A</option>
              </select>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={handleExcel} size="sm" className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="export-excel-btn">
                <FileSpreadsheet className="w-4 h-4" /> Exportar Excel
              </Button>
              <Button onClick={handlePdf} size="sm" className="bg-[#0A2647] hover:bg-[#0e3260] gap-2" data-testid="export-pdf-btn">
                <FileText className="w-4 h-4" /> Exportar PDF
              </Button>
              {(f.birthday_month || f.birthday_days) && (
                <>
                  <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-lg">
                    <Cake className="w-3 h-3" />
                    {f.birthday_days
                      ? `Fazem aniversário nos próximos ${f.birthday_days} dias`
                      : `Aniversariantes de ${MONTHS[f.birthday_month - 1]}`}
                  </span>
                  <Button onClick={() => setBirthdayOpen(true)} size="sm"
                    className="bg-[#C5A059] hover:bg-[#d4af61] text-[#0A2647] gap-2 font-semibold"
                    data-testid="birthday-offer-btn">
                    <Gift className="w-4 h-4" /> Enviar parabéns com desconto
                  </Button>
                </>
              )}
            </div>
          </div>

          {birthdayOpen && (
            <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" data-testid="birthday-modal">
              <div className="bg-white rounded-2xl w-full max-w-lg p-5 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-[#0A2647] flex items-center gap-2">
                      <Gift className="w-5 h-5 text-[#C5A059]" /> Oferta de aniversário
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {data.items.length} cliente(s) {f.birthday_days
                        ? `que fazem aniversário nos próximos ${f.birthday_days} dias`
                        : `de ${MONTHS[f.birthday_month - 1]}`} vão receber no WhatsApp.
                    </p>
                  </div>
                  <button onClick={() => setBirthdayOpen(false)} className="p-1 hover:bg-slate-100 rounded" data-testid="close-birthday">
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-600">Qual é o mimo?</label>
                  <input value={discount} onChange={e => setDiscount(e.target.value)}
                    placeholder="Ex: 20% de desconto, um café grátis, um pão de queijo"
                    className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    data-testid="birthday-discount" />
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-600">Código do cupom (você define)</label>
                  <input value={coupon} onChange={e => setCoupon(e.target.value.toUpperCase())}
                    placeholder="ANIVERSARIO"
                    className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono"
                    data-testid="birthday-coupon" />
                  <p className="text-[11px] text-slate-400 mt-1">
                    Cada cliente recebe esse código com 4 números no fim (ex: {(coupon || 'ANIVERSARIO')}-4821), para você conferir quem voltou.
                  </p>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-600">Mensagem</label>
                  <textarea value={birthdayMsg} onChange={e => setBirthdayMsg(e.target.value)} rows={4}
                    className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    data-testid="birthday-message" />
                  <p className="text-[11px] text-slate-400 mt-1">
                    Usamos <code>[nome]</code>, <code>[empresa]</code> e <code>[desconto]</code> automaticamente.
                  </p>
                </div>

                <div className="flex justify-end gap-2 pt-1">
                  <Button variant="outline" size="sm" onClick={() => setBirthdayOpen(false)}>Cancelar</Button>
                  <Button onClick={sendBirthday} disabled={sendingBirthday || !data.items.length}
                    size="sm" className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="send-birthday-btn">
                    {sendingBirthday ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
                    Enviar para {data.items.length}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
            ) : !data.items.length ? (
              <div className="py-16 px-6 text-center" data-testid="contacts-empty">
                <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                {data.total ? (
                  <>
                    <p className="text-slate-600 font-medium">Nenhum contato com esses filtros.</p>
                    <p className="text-sm text-slate-400 mt-1">Você tem {data.total} contato(s) no total — limpe os filtros para ver todos.</p>
                  </>
                ) : (
                  <>
                    <p className="text-slate-600 font-medium">Nenhum contato coletado ainda.</p>
                    <p className="text-sm text-slate-400 mt-1">
                      Quando seus clientes avaliarem pelo QR Code, os dados aparecerão aqui.
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="contacts-table">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-4 py-3">Nome</th>
                      <th className="text-left px-4 py-3">WhatsApp</th>
                      <th className="text-left px-4 py-3">E-mail</th>
                      <th className="text-left px-4 py-3">Nascimento</th>
                      <th className="text-center px-4 py-3">Avaliação</th>
                      <th className="text-left px-4 py-3">Cupom</th>
                      <th className="text-left px-4 py-3">Cadastro</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.items.map(c => (
                      <tr key={c.id} className="hover:bg-slate-50" data-testid={`contact-row-${c.id}`}>
                        <td className="px-4 py-3 font-medium text-slate-800">{c.name}</td>
                        <td className="px-4 py-3">
                          <a href={`https://wa.me/55${c.whatsapp.replace(/^55/, '')}`} target="_blank" rel="noopener noreferrer"
                            className="text-emerald-600 hover:underline">{fmtPhone(c.whatsapp)}</a>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{c.email}</td>
                        <td className="px-4 py-3 text-slate-600">
                          {fmtDate(c.birth_date)}
                          {c.days_to_birthday !== undefined && c.days_to_birthday !== null && (
                            <span className="ml-1 text-[11px] text-amber-600 font-semibold">
                              {c.days_to_birthday === 0 ? '· é hoje!' : `· em ${c.days_to_birthday}d`}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${c.rating_type === 'positive' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                            {c.rating_type === 'positive' ? 'Positiva' : 'Negativa'}
                          </span>
                        </td>
                        <td className="px-4 py-3" data-testid={`coupon-cell-${c.id}`}>
                          {c.coupon_code ? (
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs text-slate-700">{c.coupon_code}</span>
                              {c.coupon_redeemed_at ? (
                                <button onClick={() => toggleRedeem(c)}
                                  className="text-[10px] font-bold uppercase bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full hover:bg-emerald-200"
                                  title={`Voltou em ${fmtDate(c.coupon_redeemed_at)} — clique para desmarcar`}
                                  data-testid={`coupon-used-${c.id}`}>
                                  Voltou ✓
                                </button>
                              ) : (
                                <button onClick={() => toggleRedeem(c)}
                                  className="text-[10px] font-bold uppercase bg-[#C5A059] text-[#0A2647] px-2 py-0.5 rounded-full hover:bg-[#d4af61]"
                                  title="Marcar que o cliente voltou e usou o cupom"
                                  data-testid={`coupon-redeem-${c.id}`}>
                                  Marcar uso
                                </button>
                              )}
                            </div>
                          ) : <span className="text-xs text-slate-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-slate-500">{fmtDate(c.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>
      <HelpButton ambiente="dono" tela="clientes" />
    </div>
  );
};

export default MyCustomers;
